<script lang="ts">
	import { Modal, Button, ButtonGroup, ListItem, Text } from '@Shelly/shelly-design-system';
	import type { FlowSimulationResult } from '../helpers/flowTestSimulation';

	export let showModal = false;
	export let isMobile = false;
	export let result: FlowSimulationResult | null = null;
	export let onClose: () => void;
	export let onFocusNode: (nodeId: string) => void;
	export let scopeLabel = 'Whole flow';
	export let validationErrors = 0;
	export let validationWarnings = 0;
	export let isBusy = false;
	export let canTestSelection = false;
	export let onPreview: () => void = () => undefined;
	export let onTestSelection: () => void = () => undefined;
	export let onTestIsland: () => void = () => undefined;
	export let onRun: () => void = () => undefined;

	const statusIcons = {
		matched: 'fa-circle-check',
		'not-matched': 'fa-circle-xmark',
		executed: 'fa-play',
		assumed: 'fa-flask',
	};
</script>

<Modal
	{showModal}
	title="Test & debug"
	type={isMobile ? 'fullScreen' : 'default'}
	backdropClose={false}
	on:close={onClose}
>
	<svelte:fragment slot="content">
		<div class="test-report">
			<div class="debug-actions">
				<ButtonGroup aria-label="Flow test actions">
					<Button
						variant="secondary"
						size="m"
						icon="fas fa-route"
						title="Preview the whole Flow"
						on:click={onPreview}
						disabled={isBusy}
					>
						Preview
					</Button>
					<Button
						variant="secondary"
						size="m"
						icon="fas fa-location-crosshairs"
						title="Preview from the selected card"
						on:click={onTestSelection}
						disabled={isBusy || !canTestSelection}
					>
						From here
					</Button>
					<Button
						variant="secondary"
						size="m"
						icon="fas fa-object-group"
						title="Preview the connected section"
						on:click={onTestIsland}
						disabled={isBusy || !canTestSelection}
					>
						This island
					</Button>
					<Button
						variant="success"
						size="m"
						icon="fas fa-play"
						title="Run the Flow and execute its actions"
						on:click={onRun}
						disabled={isBusy || validationErrors > 0}>Run now</Button
					>
				</ButtonGroup>
			</div>
			<div class="validation-summary" class:has-errors={validationErrors > 0}>
				<i class="fas {validationErrors ? 'fa-triangle-exclamation' : 'fa-circle-check'}"
				></i><span
					><strong
						>{validationErrors
							? `${validationErrors} error${validationErrors === 1 ? '' : 's'}`
							: 'Ready to run'}</strong
					><small
						>{validationWarnings} warning{validationWarnings === 1 ? '' : 's'} · {scopeLabel}</small
					></span
				>
			</div>
			<div class="preview-notice">
				<i class="fas fa-shield-halved" aria-hidden="true"></i>
				<div>
					<strong>Nothing was executed</strong>
					<span>This preview explains the path the flow would take.</span>
				</div>
			</div>

			{#if result?.steps.length}
				<ol class="step-list" aria-label="Simulated execution path">
					{#each result.steps as step, index (`${step.nodeId}-${index}`)}
						<li>
							<ListItem
								id={`flow-test-step-${index}`}
								aria-label={`Open ${step.label}`}
								viewType="fullColored"
								listItemBgColor="--color-1"
								icon={`fas ${statusIcons[step.status]}`}
								title={`${index + 1}. ${step.label} · ${step.status === 'not-matched' ? 'not matched' : step.status}`}
								subtitle={`${step.detail}${step.observedValue !== undefined ? ` · Observed: ${String(step.observedValue)}` : ''} · ${step.elapsedMs} ms`}
								actionBtn
								on:click={() => onFocusNode(step.nodeId)}
							/>
						</li>
					{/each}
				</ol>
			{:else}
				<Text variant="secondary" size="m">There is no executable path to preview.</Text>
			{/if}

			{#if result?.values.length}
				<div class="value-tray">
					<div>
						<strong>Generated values</strong><span>Producer → consumers on this flow</span>
					</div>
					{#each result.values as value}<ListItem
							id={`flow-value-${value.name}`}
							aria-label={`Open producer for ${value.name}`}
							viewType="fullColored"
							listItemBgColor="--color-1"
							icon="fas fa-tag"
							title={value.name}
							subtitle={`${String(value.value ?? 'empty')} · used by ${value.consumerNodeIds.length} card${value.consumerNodeIds.length === 1 ? '' : 's'}`}
							actionBtn
							on:click={() => onFocusNode(value.producerNodeId)}
						/>{/each}
				</div>
			{/if}

			{#if result?.messages.length}
				<div class="test-notes">
					<strong>Preview notes</strong>
					{#each result.messages as message}<span>{message}</span>{/each}
				</div>
			{/if}
		</div>
	</svelte:fragment>

	<div slot="footer" class="modal-footer">
		<Button variant="primary" on:click={onClose}>Done</Button>
	</div>
</Modal>

<style>
	.test-report {
		box-sizing: border-box;
		width: 100%;
		max-width: 100%;
		display: grid;
		gap: 14px;
	}
	.debug-actions {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 7px;
	}
	.validation-summary span,
	.value-tray > div {
		display: flex;
		min-width: 0;
		flex-direction: column;
	}
	.validation-summary small,
	.value-tray span {
		color: #94a3b8;
		font-size: 9px;
	}
	.validation-summary {
		display: flex;
		align-items: center;
		gap: 9px;
		padding: 9px 11px;
		border: 1px solid rgba(74, 222, 128, 0.25);
		border-radius: 9px;
		background: rgba(34, 197, 94, 0.08);
		color: #86efac;
	}
	.validation-summary.has-errors {
		border-color: rgba(248, 113, 113, 0.3);
		background: rgba(239, 68, 68, 0.08);
		color: #fca5a5;
	}
	.value-tray {
		display: grid;
		grid-template-columns: minmax(120px, 0.7fr) repeat(2, minmax(0, 1fr));
		gap: 7px;
		padding: 10px;
		border: 1px solid rgba(167, 139, 250, 0.25);
		border-radius: 10px;
		background: rgba(139, 92, 246, 0.07);
	}
	.preview-notice {
		display: flex;
		gap: 12px;
		padding: 12px;
		border-radius: 10px;
		color: #bfdbfe;
		background: rgba(37, 99, 235, 0.12);
		border: 1px solid rgba(96, 165, 250, 0.3);
	}
	.preview-notice > i {
		margin-top: 3px;
	}
	.preview-notice div,
	.test-notes {
		display: grid;
		gap: 3px;
	}
	.preview-notice span,
	.test-notes span {
		color: #a1a1aa;
		font-size: 12px;
		line-height: 1.4;
	}
	.step-list {
		list-style: none;
		display: grid;
		gap: 8px;
		padding: 0;
		margin: 0;
	}
	.test-notes {
		padding: 10px 12px;
		border-left: 2px solid #a855f7;
		background: rgba(168, 85, 247, 0.08);
	}
	.modal-footer {
		display: flex;
		justify-content: flex-end;
	}
	@media (max-width: 760px) {
		.test-report {
			width: 100%;
		}
		.debug-actions {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
		.value-tray {
			grid-template-columns: 1fr;
		}
	}
</style>
